import React, { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import * as inventoryService from "../../services/inventoryService";
import UsageOverTimeChart from "../../components/InventoryAnalytics/UsageOverTimeChart";
import TopUsedItemsChart from "../../components/InventoryAnalytics/TopUsedItemsChart";
import UsageByCategoryChart from "../../components/InventoryAnalytics/UsageByCategoryChart";
import { formatDecimal } from "../../utils/numberFormat";
import { getVendorCurrency } from "../../utils/currency";

const slowStockThStyle = {
  background: "#F9FAFB",
  fontWeight: "600",
  fontSize: "12px",
  color: "#374151",
  padding: "8px 12px",
  borderBottom: "1px solid #E5E7EB",
  textAlign: "left",
  position: "sticky",
  top: 0,
};

const slowStockTdStyle = {
  padding: "8px 12px",
  fontSize: "13px",
  color: "#374151",
  borderBottom: "1px solid #F3F4F6",
};

// Mirrors the structure of src/components/Analytics/AnalyticsDashboard.jsx
// (own data-fetch, own chart wrappers) in a sibling InventoryAnalytics
// folder, using recharts - the only chart library in this app. Per-item
// drill-down reuses InventoryItemDetail's history timeline (link out) rather
// than duplicating a drill-down view.
function InventoryAnalyticsDashboard() {
  const [usage, setUsage] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [margin, setMargin] = useState([]);
  const [valuation, setValuation] = useState([]);
  const [slowStock, setSlowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      inventoryService.getUsageOverTime(),
      inventoryService.getTopUsedItems({ limit: 10 }),
      inventoryService.getUsageByCategory(),
      inventoryService.getMargin(),
      inventoryService.getValuation(),
      inventoryService.getSlowDeadStock(),
    ])
      .then(([usageRes, topRes, catRes, marginRes, valRes, slowRes]) => {
        setUsage(usageRes?.data || []);
        setTopItems(topRes?.data || []);
        setByCategory(catRes?.data || []);
        setMargin(marginRes?.data || []);
        setValuation(valRes?.data || []);
        setSlowStock(slowRes?.data || []);
      })
      .catch((err) => toast.error(err.message || "Could not load analytics"))
      .finally(() => setLoading(false));
  }, []);

  const currency = getVendorCurrency();
  const totalValuation = valuation.reduce((sum, i) => sum + Number(i.stock_value || 0), 0);
  const totalMarginRevenue = margin.reduce((sum, m) => sum + Number(m.parts_revenue || 0), 0);
  const totalMarginCost = margin.reduce((sum, m) => sum + Number(m.parts_cost || 0), 0);

  if (loading) return <div className="p-4">Loading…</div>;

  return (
    <div className="p-3">
      <h4 className="mb-3">Inventory Analytics</h4>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card body>
            <h6>Consumption Over Time</h6>
            <UsageOverTimeChart data={usage} />
          </Card>
        </Col>
        <Col md={6}>
          <Card body>
            <h6>Top Used Items</h6>
            <TopUsedItemsChart data={topItems} />
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card body>
            <h6>Usage by Category (cost)</h6>
            <UsageByCategoryChart data={byCategory} />
          </Card>
        </Col>
        <Col md={6}>
          <Card body>
            <h6>Margin Summary</h6>
            <div className="d-flex justify-content-around text-center py-3">
              <div>
                <div className="text-muted" style={{ fontSize: 12 }}>Parts Revenue</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{currency} {totalMarginRevenue.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: 12 }}>Parts Cost</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{currency} {totalMarginCost.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: 12 }}>Margin</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--button-color)" }}>
                  {currency} {(totalMarginRevenue - totalMarginCost).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="text-muted" style={{ fontSize: 11 }}>
              Reads stored per-line unit_price/unit_cost values only - never recalculated from current item cost.
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md={6}>
          <Card body>
            <h6>Stock Valuation Summary</h6>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{currency} {totalValuation.toFixed(2)}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>Total stock value at cost, across {valuation.length} active items</div>
          </Card>
        </Col>
        <Col md={6}>
          <Card body>
            <h6>Slow / Dead Stock</h6>
            <div style={{ maxHeight: 220, overflowY: "auto", borderRadius: "8px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
                <thead>
                  <tr>
                    <th style={slowStockThStyle}>Item</th>
                    <th style={slowStockThStyle}>Qty</th>
                    <th style={slowStockThStyle}>Value</th>
                    <th style={slowStockThStyle}>Last movement</th>
                  </tr>
                </thead>
                <tbody>
                  {slowStock.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ ...slowStockTdStyle, textAlign: "center", color: "#9CA3AF" }}>No slow/dead stock detected.</td>
                    </tr>
                  )}
                  {slowStock.map((row) => (
                    <tr
                      key={row.id}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      style={{ transition: "background 0.15s" }}
                    >
                      <td style={slowStockTdStyle}>{row.name}</td>
                      <td style={slowStockTdStyle}>{formatDecimal(row.current_qty)}</td>
                      <td style={slowStockTdStyle}>{formatDecimal(row.stock_value)}</td>
                      <td style={slowStockTdStyle}>{row.last_movement_at ? new Date(row.last_movement_at).toLocaleDateString() : "Never"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default InventoryAnalyticsDashboard;
